import { QRCodeSVG } from "qrcode.react";
import { animationCSS, bgCSS, buildGradientString, resolveDataValue, shadowCSS } from "./CssHelper";
import { applyListFunctions, resolveValue } from "./DynamicValueResolver";
import { T } from "./exports";
import { buildYouTubeSrc } from "./leafHelpers";
import { ResizeHandles } from "./ResizeHelper";
import { Clock } from "./UIHelper";
import { Model3DViewer } from "./Model3DViewer";
import { CatalogLeaf } from "../Widgets/CatalogLeaf";
import { CATALOG_TYPES } from "../Widgets/catalog";
import { KioskIcon } from "../Widgets/KioskIcon";
import { KeyboardElement } from "../Widgets/OnScreenKeyboard";

export function LeafView({node,selected,onMouseDown,onResizeDown,dataCtx,runtimeVars,onVarChange,globalSourceStore}){
  const s=node.styles;
  const txt=resolveValue(node,dataCtx,runtimeVars);
  const base={width:"100%",height:"100%",boxSizing:"border-box"};
  const shadow=shadowCSS(s);
  const flt=s.blur?`blur(${s.blur}px)`:"none";
  let inner;

  if(["text","clock","ticker"].includes(node.type)){
    const justifyMap={left:"flex-start",center:"center",right:"flex-end"};
    const rawText=node.type==="clock"?null:(txt||node.staticText||"");
    const autoFs=s.fontAutoSize&&rawText?Math.min(s.fontSize,Math.max(8,Math.floor((s.width-12)/(rawText.length||1)*1.6))):s.fontSize;
    const overflow=s.textOverflow||"clip";
    const textStyle={fontSize:autoFs,fontWeight:s.fontWeight,fontFamily:s.fontFamily};
    if(overflow==="ellipsis"){textStyle.overflow="hidden";textStyle.textOverflow="ellipsis";textStyle.whiteSpace="nowrap";textStyle.display="block";textStyle.maxWidth="100%";}
    else if(overflow==="wrap"){textStyle.whiteSpace="pre-wrap";textStyle.wordBreak="break-word";textStyle.display="block";textStyle.maxWidth="100%";}
    else{textStyle.whiteSpace="nowrap";}
    if(s.textGradientEnabled&&s.textGradientStops&&s.textGradientStops.length>=2){
      textStyle.background=buildGradientString(s.textGradientStops,s.textGradientAngle);
      textStyle.WebkitBackgroundClip="text";
      textStyle.WebkitTextFillColor="transparent";
      textStyle.backgroundClip="text";
    }else{
      textStyle.color=s.color;
    }
    inner=<div style={{...base,backgroundColor:bgCSS(s),borderRadius:s.borderRadius,display:"flex",alignItems:"center",justifyContent:justifyMap[s.textAlign]||"flex-start",overflow:"hidden",padding:"0 6px",boxShadow:shadow,filter:flt}}>
      <span style={textStyle}>
        {node.type==="clock"?<Clock/>:rawText}
      </span>
    </div>;
  }
  else if(node.type==="button"){
    const btnLabel=resolveValue(node,dataCtx,runtimeVars)||node.staticText||"Button";
    const btnOverflow=s.textOverflow||"clip";
    const btnAutoFs=s.fontAutoSize&&btnLabel?Math.min(s.fontSize,Math.max(8,Math.floor((s.width-12)/(btnLabel.length||1)*1.6))):s.fontSize;
    const buttonTextStyle={fontSize:btnAutoFs,fontWeight:s.fontWeight,fontFamily:s.fontFamily};
    if(btnOverflow==="ellipsis"){buttonTextStyle.overflow="hidden";buttonTextStyle.textOverflow="ellipsis";buttonTextStyle.whiteSpace="nowrap";buttonTextStyle.display="block";buttonTextStyle.maxWidth="100%";}
    else if(btnOverflow==="wrap"){buttonTextStyle.whiteSpace="pre-wrap";buttonTextStyle.wordBreak="break-word";buttonTextStyle.display="block";buttonTextStyle.maxWidth="100%";}
    if(s.textGradientEnabled&&s.textGradientStops&&s.textGradientStops.length>=2){
      buttonTextStyle.background=buildGradientString(s.textGradientStops,s.textGradientAngle);
      buttonTextStyle.WebkitBackgroundClip="text";
      buttonTextStyle.WebkitTextFillColor="transparent";
      buttonTextStyle.backgroundClip="text";
    }else{
      buttonTextStyle.color=s.color;
    }
    const hasIcon=s.iconEnabled&&s.icon;
    const iconEl=hasIcon?<KioskIcon name={s.icon} size={s.fontSize} color={s.color||"#fff"}/>:null;
    const gap=s.iconGap||6;
    // special fn badge
    const sfnBadge=node.specialFn&&node.specialFn!=="none"
      ?<div style={{position:"absolute",top:2,left:4,fontSize:6,color:"rgba(255,255,255,0.6)",background:"rgba(0,0,0,0.35)",borderRadius:2,padding:"0 3px",lineHeight:"14px"}}>{node.specialFn==="translate"?"🌐":node.specialFn==="speak"?"🔊":""}</div>
      :null;
    inner=<div style={{...base,fontSize:s.fontSize,fontWeight:s.fontWeight,fontFamily:s.fontFamily,background:bgCSS(s),borderRadius:s.borderRadius,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:shadow,filter:flt,position:"relative",gap:hasIcon?gap:0}}>
      {sfnBadge}
      {hasIcon&&s.iconPosition!=="right"&&iconEl}
      <span style={buttonTextStyle}>{btnLabel}</span>
      {hasIcon&&s.iconPosition==="right"&&iconEl}
      {node.onClick?.length>0&&<div style={{position:"absolute",bottom:2,right:4,fontSize:7,color:"rgba(255,255,255,0.5)"}}>⚡{node.onClick.length}</div>}
    </div>;
  }
  else if(node.type==="image")
    inner=<div style={{...base,backgroundColor:"transparent",borderRadius:s.borderRadius,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",fontSize:10,color:"rgba(255,255,255,0.25)",boxShadow:shadow,filter:flt}}>
      {(node.dynamic&&txt)||node.staticText?<img src={txt||node.staticText} alt="" style={{width:"100%",height:"100%",objectFit:s.objectFit||"cover",display:"block"}}/>:<span style={{pointerEvents:"none"}}>⬚</span>}
    </div>;
  else if(node.type==="input"){
    const varVal=node.inputVar&&runtimeVars?runtimeVars[node.inputVar]:"";
    inner=<div style={{...base,backgroundColor:bgCSS(s),borderRadius:s.borderRadius,border:`${s.borderWidth||1}px solid ${s.borderColor||T.border}`,display:"flex",alignItems:"center",padding:"0 8px",boxShadow:shadow,filter:flt,position:"relative"}}>
      {node.inputVar
        ?<input value={varVal||""} placeholder={node.inputPlaceholder||node.inputVar}
            onChange={e=>onVarChange&&onVarChange(node.inputVar,e.target.value)}
            onClick={e=>e.stopPropagation()}
            style={{flex:1,background:"transparent",border:"none",outline:"none",color:s.color||T.text,fontSize:s.fontSize,fontFamily:s.fontFamily,width:"100%"}}/>
        :<span style={{color:T.textSub,fontSize:s.fontSize,fontFamily:s.fontFamily}}>{node.staticText||node.inputPlaceholder||"Input..."}</span>
      }
      {node.inputVar&&<span style={{position:"absolute",top:1,right:3,fontSize:6,color:T.blue,fontWeight:700,pointerEvents:"none"}}>${node.inputVar}</span>}
    </div>;
  }
  else if(node.type==="qr"){
    const qrValue=(node.dynamic&&txt)||node.staticText||"";
    const qrBg=s.backgroundColor||"#ffffff";
    const qrSize=Math.max(20,Math.min(s.width,s.height)-12);
    inner=<div style={{...base,backgroundColor:qrBg,borderRadius:s.borderRadius,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",boxShadow:shadow}}>
      {qrValue
        ?<QRCodeSVG value={qrValue} size={qrSize} bgColor={qrBg} fgColor="#000000" level="M"/>
        :<div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2,color:"#36454F"}}>
            <span style={{fontSize:Math.min(24,qrSize*0.4)}}>⠿</span>
            <span style={{fontSize:8}}>QR</span>
          </div>
      }
    </div>;
  }
  else if(node.type==="googleMap"){
    const gmApiKey=node.googleMapApiKey||"";
    const sourceId=node.googleMapSourceId||"";
    const markersDataKey=node.googleMapMarkersDataKey||"";
    const latCol=node.googleMapLatColumn||"lat";
    const lngCol=node.googleMapLngColumn||"lng";
    const rawTitleCol=node.googleMapTitleColumn||"title";
    const titleCol=rawTitleCol.replace(/\$(\w+)/g,(_,varName)=>runtimeVars?.[varName]??varName);
    let sourceRows=(sourceId&&globalSourceStore&&Array.isArray(globalSourceStore[sourceId]))
      ?globalSourceStore[sourceId]
      :(node.googleMapExcelData&&Array.isArray(node.googleMapExcelData))
        ?node.googleMapExcelData
        :(node.googleMapMarkers||[]);
    if(markersDataKey&&dataCtx!==null){
      const resolvedMarkers=resolveDataValue(markersDataKey,dataCtx);
      if(Array.isArray(resolvedMarkers)) sourceRows=resolvedMarkers;
    }
    const processedRows=applyListFunctions(sourceRows,node.googleMapListFn,runtimeVars);
    const gmMarkers=(processedRows||[])
      .filter(r=>{
        const latRaw=(r?.[latCol]??r?.lat);
        const lngRaw=(r?.[lngCol]??r?.lng);
        return latRaw!=null&&lngRaw!=null;
      })
      .map((r,idx)=>({
        lat:parseFloat(r?.[latCol]??r?.lat),
        lng:parseFloat(r?.[lngCol]??r?.lng),
        title:resolveDataValue(titleCol,r)??r?.title??`Location ${idx+1}`,
        data:r?.data??r,
      }));
    const gmCenter=(node.googleMapCenterLat&&node.googleMapCenterLng)?{lat:node.googleMapCenterLat,lng:node.googleMapCenterLng,title:node.googleMapCenterTitle||"Center"}:null;
    const gmMapOpts={showRoadNames:node.googleMapShowRoadNames!==false,showRivers:node.googleMapShowRivers!==false,showWaterBodies:node.googleMapShowWaterBodies!==false,showPoi:node.googleMapShowPoi!==false,showTransit:node.googleMapShowTransit!==false,showScale:node.googleMapShowScale||false,mapType:node.googleMapMapType||"roadmap",mapTheme:node.googleMapMapTheme||"default",customMapStyles:node.googleMapCustomMapStyles||""};
    // lazy import GoogleMapWidget only when we have an API key
    if(gmApiKey){
      const GoogleMapWidgetLazy=require("../GoogleMapWidget").default;
      inner=<div style={{width:"100%",height:"100%",borderRadius:s.borderRadius,overflow:"hidden",boxShadow:shadow}}>
        <GoogleMapWidgetLazy apiKey={gmApiKey} startingLat={node.googleMapStartingLat||40.7128} startingLng={node.googleMapStartingLng||-74.0060} zoom={node.googleMapZoom||12} markers={gmMarkers} selectedMarkerIndex={null} onMarkerSelect={()=>{}} showInfoCard={false} centerLocation={gmCenter} showRoutes={false} mapOptions={gmMapOpts} markerType={node.googleMapMarkerType||"circle"} centerLabelText={node.googleMapCenterLabelText||""} centerLabelBg={node.googleMapCenterLabelBg||"#1a2228"} centerLabelColor={node.googleMapCenterLabelColor||"#ffffff"} centerLabelBorderRadius={node.googleMapCenterLabelBorderRadius||20} centerLabelFontSize={node.googleMapCenterLabelFontSize||12} centerLabelBorderColor={node.googleMapCenterLabelBorderColor||"rgba(255,255,255,0.2)"} showMarkerLabel={node.googleMapShowMarkerLabel||false} markerLabelBg={node.googleMapMarkerLabelBg||"rgba(15,22,28,0.88)"} markerLabelColor={node.googleMapMarkerLabelColor||"#ffffff"} markerLabelBorderRadius={node.googleMapMarkerLabelBorderRadius||10} markerLabelFontSize={node.googleMapMarkerLabelFontSize||10}/>
      </div>;
    } else {
      inner=<div style={{...base,background:"#1e2b31",borderRadius:s.borderRadius,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textSub,boxShadow:shadow,gap:4}}>
        <span style={{fontSize:20}}>🗺</span>
        <span>Google Map</span>
        <span style={{fontSize:8,color:T.textMute}}>Add API key in Content panel</span>
        {gmMarkers.length>0&&<span style={{fontSize:8,color:T.green}}>{gmMarkers.length} locations loaded</span>}
        {gmCenter&&<span style={{fontSize:8,color:T.blue}}>📍 Center set</span>}
      </div>;
    }
  }
  else if(node.type==="video"){
    const videoSrc=(node.dynamic&&txt)||node.staticText||"";
    if(videoSrc){
      inner=<div style={{...base,borderRadius:s.borderRadius,overflow:"hidden",boxShadow:shadow,filter:flt,position:"relative"}}>
        <video
          src={videoSrc}
          poster={node.videoPoster||undefined}
          autoPlay={node.videoAutoPlay||false}
          controls={node.videoControls!==false}
          loop={node.videoLoop||false}
          muted={node.videoMuted||false}
          playsInline={node.videoPlaysinline!==false}
          style={{width:"100%",height:"100%",objectFit:s.objectFit||"cover",display:"block",borderRadius:s.borderRadius}}
        />
        {node.onEnd?.length>0&&<div style={{position:"absolute",bottom:2,right:4,fontSize:7,color:T.yellow,background:"rgba(0,0,0,0.5)",borderRadius:2,padding:"1px 3px"}}>⚡{node.onEnd.length}</div>}
      </div>;
    } else {
      inner=<div style={{...base,background:"#0f172a",borderRadius:s.borderRadius,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textSub,boxShadow:shadow,gap:4,position:"relative"}}>
        <span style={{fontSize:24,opacity:0.6}}>▶</span>
        <span>Video</span>
        <span style={{fontSize:8,color:T.textMute}}>Paste video URL in Content</span>
        {node.onEnd?.length>0&&<div style={{position:"absolute",bottom:2,right:4,fontSize:7,color:T.yellow}}>⚡{node.onEnd.length}</div>}
      </div>;
    }
  }
  else if(node.type==="youtube"){
    const ytSrc=buildYouTubeSrc({...node,staticText:(node.dynamic&&txt)||node.staticText||""});
    if(ytSrc){
      inner=<div style={{...base,borderRadius:s.borderRadius,overflow:"hidden",boxShadow:shadow,filter:flt,position:"relative"}}>
        <iframe
          src={ytSrc}
          title="YouTube video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          style={{width:"100%",height:"100%",display:"block",border:"none"}}
        />
        {/* Covers the title / channel chip YouTube injects at the top */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:52,pointerEvents:"none",background:s.backgroundColor||"#000",borderRadius:`${s.borderRadius||0}px ${s.borderRadius||0}px 0 0`}}/>
        {node.onEnd?.length>0&&<div style={{position:"absolute",bottom:2,right:4,fontSize:7,color:T.yellow,background:"rgba(0,0,0,0.6)",borderRadius:2,padding:"1px 3px",pointerEvents:"none"}}>⚡{node.onEnd.length}</div>}
      </div>;
    } else {
      inner=<div style={{...base,background:"#0f0f0f",borderRadius:s.borderRadius,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textSub,boxShadow:shadow,gap:4,position:"relative"}}>
        <span style={{fontSize:24,color:"#ff0000"}}>▶</span>
        <span>YouTube</span>
        <span style={{fontSize:8,color:T.textMute}}>Paste YouTube URL in Content</span>
        {node.onEnd?.length>0&&<div style={{position:"absolute",bottom:2,right:4,fontSize:7,color:T.yellow}}>{node.onEnd.length}</div>}
      </div>;
    }
  }
  else if(node.type==="checkbox"){
    const isChecked=node.checkboxChecked||false;
    const label=txt;
    const size=node.checkboxSize||18;
    const accent=node.checkboxAccentColor||"#6366f1";
    const pos=node.checkboxLabelPosition||"right";
    inner=<div style={{...base,display:"flex",alignItems:"center",gap:6,flexDirection:pos==="left"?"row-reverse":"row",padding:"0 4px"}}>
      <div style={{width:size,height:size,borderRadius:3,border:`2px solid ${isChecked?accent:"#36454F"}`,background:isChecked?accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
        {isChecked&&<span style={{color:"#fff",fontSize:Math.floor(size*0.65),lineHeight:1,pointerEvents:"none"}}>✓</span>}
      </div>
      {label&&<span style={{fontSize:s.fontSize||14,fontWeight:s.fontWeight||"400",fontFamily:s.fontFamily||"Inter",color:s.color||"#ffffff",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</span>}
    </div>;
  }
  else if(node.type==="divider"){
    const isV=node.dividerOrientation==="vertical";
    const thickness=node.dividerThickness||2;
    const color=node.dividerColor||"#36454F";
    const dStyle=node.dividerStyle||"solid";
    inner=<div style={{...base,display:"flex",alignItems:"center",justifyContent:"center"}}>
      {isV
        ?<div style={{width:thickness,height:"100%",background:dStyle==="solid"?color:"transparent",borderLeft:dStyle!=="solid"?`${thickness}px ${dStyle} ${color}`:"none"}}/>
        :<div style={{width:"100%",height:thickness,background:dStyle==="solid"?color:"transparent",borderTop:dStyle!=="solid"?`${thickness}px ${dStyle} ${color}`:"none"}}/>
      }
    </div>;
  }
  else if(node.type==="table"){
    const cols=node.tableColumns||[];
    const rows=node.tableRows||[];
    const showHeader=node.tableShowHeader!==false;
    const headerBg=node.tableHeaderBg||"#36454F";
    const headerColor=node.tableHeaderColor||"#ffffff";
    const rowBg=node.tableRowBg||"#2a363d";
    const altRowBg=node.tableAltRowBg||"#222d33";
    const rowColor=node.tableRowColor||"#f0f4f5";
    const borderColor=node.tableBorderColor||"#36454F";
    const striped=node.tableStriped!==false;
    const fs=s.fontSize||12;
    inner=<div style={{...base,overflow:"hidden",borderRadius:s.borderRadius}}>
      <table style={{width:"100%",height:"100%",borderCollapse:"collapse",fontSize:fs,fontFamily:s.fontFamily||"Inter",tableLayout:"fixed"}}>
        {showHeader&&cols.length>0&&<thead><tr>{cols.map((c,ci)=><th key={ci} style={{background:headerBg,color:headerColor,padding:"4px 6px",borderBottom:`1px solid ${borderColor}`,textAlign:"left",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.label||c.key}</th>)}</tr></thead>}
        <tbody>{rows.map((r,ri)=><tr key={ri}>{cols.map((c,ci)=><td key={ci} style={{background:striped&&ri%2===1?altRowBg:rowBg,color:rowColor,padding:"4px 6px",borderBottom:`1px solid ${borderColor}22`,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{String(r[c.key]??"")} </td>)}</tr>)}</tbody>
      </table>
    </div>;
  }
  else if(node.type==="slider"){
    const val=node.sliderDefaultValue??50;
    const min=node.sliderMin??0;
    const max=node.sliderMax??100;
    const accent=node.sliderAccentColor||"#6366f1";
    const label=node.sliderLabel||"";
    inner=<div style={{...base,display:"flex",flexDirection:"column",justifyContent:"center",gap:4,padding:"0 6px"}}>
      {label&&<div style={{fontSize:(s.fontSize||12)-2,color:s.color||"#ffffff",fontFamily:s.fontFamily||"Inter",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</div>}
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        <input type="range" min={min} max={max} value={val} readOnly onChange={()=>{}}
          style={{flex:1,accentColor:accent,cursor:"default",pointerEvents:"none"}}/>
        {node.sliderShowValue!==false&&<span style={{fontSize:(s.fontSize||12)-2,color:s.color||"#f0f4f5",fontFamily:"monospace",minWidth:28,textAlign:"right"}}>{val}</span>}
      </div>
    </div>;
  }
  else if(node.type==="quiz"){
    const filtered=(node.quizQuestions||[]).filter(q=>!node.quizCategory||!q.category||q.category===node.quizCategory);
    const perPage=node.quizQuestionsPerPage||1;
    const preview=filtered.slice(0,perPage);
    const qOptLayout=node.quizOptionLayout||"column";
    const qOptCols=node.quizOptionColumns||2;
    const qGap=node.quizOptionGap??6;
    const optContStyle=qOptLayout==="grid"?{display:"grid",gridTemplateColumns:`repeat(${qOptCols},1fr)`,gap:qGap}:qOptLayout==="row"?{display:"flex",flexDirection:"row",flexWrap:"wrap",gap:qGap}:{display:"flex",flexDirection:"column",gap:qGap};
    const qPX=node.quizContainerPaddingX??12;
    const qPY=node.quizContainerPaddingY??10;
    const qValignMap={"top":"flex-start","center":"center","bottom":"flex-end"};
    const qQFont=node.quizQuestionFontFamily||s.fontFamily||"Inter";
    const qOFont=node.quizOptionFontFamily||s.fontFamily||"Inter";
    inner=<div style={{...base,background:s.backgroundColor||"#2a363d",borderRadius:s.borderRadius||8,overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:shadow}}>
      <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",justifyContent:qValignMap[node.quizContentValign||"top"]||"flex-start"}}>
        {preview.map((q,qi)=>(
          <div key={qi} style={{padding:`${qPY}px ${qPX}px`,borderBottom:`1px solid ${T.border}22`}}>
            <div style={{fontSize:node.quizQuestionFontSize>0?node.quizQuestionFontSize:(s.fontSize||13),fontWeight:node.quizQuestionFontWeight||"600",color:s.color||"#ffffff",fontFamily:qQFont,marginBottom:q.questionImage?4:(node.quizQuestionMarginBottom??8),textAlign:node.quizQuestionAlign||"left"}}>{q.question||`Question ${qi+1}`}</div>
            {q.questionImage&&<div style={{width:"100%",height:50,background:`url(${q.questionImage}) center/cover`,borderRadius:4,marginBottom:node.quizQuestionMarginBottom??8}}/>}
            <div style={optContStyle}>
              {(q.answerType||"mcq")==="input"
                ?<div style={{padding:"2px 6px",borderRadius:node.quizOptionBorderRadius??4,background:node.quizOptionBg||"#ffffff0d",border:`1px solid ${node.quizOptionBorderColor||T.border+"22"}`,fontSize:node.quizOptionFontSize>0?node.quizOptionFontSize:10,color:node.quizOptionColor||T.textSub,fontFamily:qOFont,fontStyle:"italic"}}>Text input answer…</div>
                :(q.options||[]).map((opt,oi)=>{const optText=typeof opt==="object"?(opt.text||""):opt;const optImg=typeof opt==="object"?opt.image:"";return(
                <div key={oi} style={{padding:"2px 6px",borderRadius:node.quizOptionBorderRadius??4,background:oi===q.correct?"#22c55e22":(node.quizOptionBg||"#ffffff0d"),border:`1px solid ${oi===q.correct?"#22c55e44":(node.quizOptionBorderColor||T.border+"22")}`,fontSize:node.quizOptionFontSize>0?node.quizOptionFontSize:10,color:oi===q.correct?T.green:(node.quizOptionColor||T.textSub),fontFamily:qOFont,overflow:"hidden"}}>
                  {optImg&&<div style={{width:"100%",height:24,background:`url(${optImg}) center/cover`,borderRadius:2,marginBottom:2}}/>}
                  {optText}
                </div>);
              })}
            </div>
          </div>
        ))}
        {filtered.length===0&&<div style={{display:"flex",alignItems:"center",justifyContent:"center",height:60,color:T.textMute,fontSize:9}}>Add questions in Content panel</div>}
      </div>
    </div>;
  }
  else if(node.type==="confetti"){
    const colors=(node.confettiColors||"#ff4757,#ffa502,#2ed573,#1e90ff,#eccc68").split(",").map(c=>c.trim()).filter(Boolean);
    const dots=Array.from({length:20},(_,i)=>({x:(i*37+13)%90,y:(i*53+7)%80,r:node.confettiSize||8,c:colors[i%colors.length],rot:(i*41)%360}));
    inner=<div style={{...base,overflow:"hidden",position:"relative",boxShadow:shadow,borderRadius:s.borderRadius||0}}>
      {dots.map((d,i)=><div key={i} style={{position:"absolute",left:`${d.x}%`,top:`${d.y}%`,width:d.r*(i%3===0?1:i%3===1?0.6:0.8),height:d.r*(i%3===0?1:i%3===1?0.6:0.8),background:d.c,borderRadius:i%2===0?"50%":2,transform:`rotate(${d.rot}deg)`,opacity:0.85}}/>)}
      <div style={{position:"absolute",bottom:4,right:6,fontSize:8,color:"rgba(255,255,255,0.4)"}}>🎉 confetti</div>
    </div>;
  }
  else if(node.type==="model3d"){
    inner=<Model3DViewer
      url={node.model3dUrl||""}
      bg={node.model3dBg||"dark"}
      bgColor={node.model3dBgColor||"#1a2228"}
      autoRotate={node.model3dAutoRotate!==false}
      cameraControls={node.model3dCameraControls!==false}
      exposure={node.model3dExposure||1}
      shadowIntensity={node.model3dShadowIntensity||1}
      borderRadius={s.borderRadius||0}
    />;
  }
  else if(node.type==="html"){
    inner=<div style={{...base,overflow:"hidden",borderRadius:s.borderRadius||4,boxShadow:shadow}}>
      {node.htmlCode
        ?<iframe
            srcDoc={node.htmlCode}
            title="html-canvas"
            sandbox="allow-scripts allow-same-origin"
            style={{width:"100%",height:"100%",border:"none",display:"block",pointerEvents:"none"}}
          />
        :<div style={{width:"100%",height:"100%",background:"#111",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4}}>
            <span style={{fontSize:20,opacity:0.4}}>{"</>"}</span>
            <span style={{fontSize:9,color:T.textMute}}>HTML Embed</span>
          </div>
      }
    </div>;
  }
  else if(node.type==="keyboard"){
    inner=<div style={{...base,filter:flt}}><KeyboardElement node={node} interactive={false}/></div>;
  }
  else if(CATALOG_TYPES.includes(node.type)){
    inner=<div style={{...base,background:bgCSS(s),borderRadius:s.borderRadius||0,overflow:"hidden",boxShadow:shadow,filter:flt}}>
      <CatalogLeaf node={node} vars={runtimeVars} globalDataStore={globalSourceStore} interactive={false}/>
    </div>;
  }
  else inner=<div style={{...base,background:T.bgCard,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:T.textSub}}>{node.type}</div>;

  const isHidden=node.visible===false;
  return(
    <div onMouseDown={e=>{e.stopPropagation();onMouseDown(e);}}
      style={{position:"absolute",left:s.x,top:s.y,width:s.width,height:s.height,opacity:(s.opacity??100)/100*(isHidden?0.3:1),zIndex:s.zIndex,outline:selected?"2px solid #818cf8":"none",outlineOffset:1,cursor:"move",userSelect:"none",boxSizing:"border-box",animation:animationCSS(s)}}>
      {inner}
      {isHidden&&<div style={{position:"absolute",top:2,right:2,fontSize:6,color:"#f59e0b",background:"rgba(0,0,0,0.75)",padding:"1px 4px",borderRadius:2,pointerEvents:"none",zIndex:9999,fontWeight:700,letterSpacing:"0.05em"}}>HIDDEN</div>}
      {selected&&<ResizeHandles w={s.width} h={s.height} onDown={onResizeDown}/>}
    </div>
  );
}